"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Skeleton } from "@/components/ui/skeleton"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/contexts/AuthContext"
import { markdownService } from "@/services/markdownService"
import MarkdownGuide from "@/components/markdown-guide"
import { Edit, Save, Eye, Loader2 } from "lucide-react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

interface MarkdownEditorProps {
  slug: string
}

export default function MarkdownEditor({ slug }: MarkdownEditorProps) {
  const [content, setContent] = useState("")
  const [title, setTitle] = useState("")
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [lastModified, setLastModified] = useState<string>("")
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false)

  const { isAuthenticated } = useAuth()
  const { toast } = useToast()

  useEffect(() => {
    loadContent()
  }, [slug])

  const loadContent = async () => {
    setIsLoading(true)
    try {
      const data = await markdownService.getMarkdownPage(slug)
      setContent(data.content)
      setTitle(data.title)
      setLastModified(data.lastModified)
    } catch (error) {
      // Si no existe en la base de datos, mostrar error
      console.error(`Página ${slug} no encontrada en la base de datos`)
      setContent("")
      setTitle("")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!isAuthenticated) return

    setIsSaving(true)
    try {
      const data = await markdownService.saveMarkdownPage({
        slug,
        title,
        content,
      })

      setLastModified(data.lastModified)
      setIsEditing(false)

      toast({
        title: "Guardado exitoso",
        description: "El contenido ha sido guardado correctamente.",
      })
    } catch (error) {
      toast({
        title: "Error al guardar",
        description: "No se pudo guardar el contenido. Inténtalo de nuevo.",
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        <Skeleton className="h-12 w-3/4 mb-6" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    )
  }

  // Si no hay contenido, mostrar mensaje de no encontrado
  if (!content && !title) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-600 mb-4">Página no encontrada</h1>
          <p className="text-gray-500 text-sm sm:text-base">Esta página no existe en la base de datos.</p>
          {isAuthenticated && (
            <Button 
              onClick={() => {
                setTitle(`Página ${slug}`)
                setContent(`# Página ${slug}\n\nEscribe tu contenido aquí...`)
                setIsEditing(true)
              }}
              className="mt-4"
            >
              Crear página
            </Button>
          )}
        </div>
      </div>
    )
  }

  // Vista para usuarios no autenticados - Markdown elegante
  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        <article className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-raime-dark-blue mb-6 sm:mb-8 break-words">{title}</h1>
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-raime-dark-blue mt-6 sm:mt-8 mb-3 sm:mb-4 border-b border-gray-200 pb-2 break-words">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-raime-dark-blue mt-4 sm:mt-6 mb-2 sm:mb-3 break-words">{children}</h2>
                ),
                h3: ({ children }) => <h3 className="text-base sm:text-lg lg:text-xl font-medium text-gray-800 mt-3 sm:mt-4 mb-2 break-words">{children}</h3>,
                p: ({ children }) => <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 break-words">{children}</p>,
                ul: ({ children }) => (
                  <ul className="list-disc list-inside space-y-2 mb-4 text-sm sm:text-base text-gray-700 pl-2">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal list-inside space-y-2 mb-4 text-sm sm:text-base text-gray-700 pl-2">{children}</ol>
                ),
                li: ({ children }) => <li className="ml-2 sm:ml-4 break-words">{children}</li>,
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="text-raime-golden-brown hover:text-raime-dark-blue underline transition-colors break-words"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ),
                strong: ({ children }) => <strong className="font-semibold text-raime-dark-blue break-words">{children}</strong>,
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-raime-golden-brown pl-3 sm:pl-4 italic text-sm sm:text-base text-gray-600 my-4 break-words">
                    {children}
                  </blockquote>
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
          {lastModified && (
            <div className="mt-6 sm:mt-8 pt-4 border-t border-gray-200 text-xs sm:text-sm text-gray-500">
              Última actualización:{" "}
              {new Date(lastModified).toLocaleDateString("es-ES", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
          )}
        </article>
      </div>
    )
  }

  // Vista para usuarios autenticados - Editor completo
  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-6xl">
      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-raime-dark-blue break-words">
            <span className="block sm:inline">Editor de Markdown - {slug}</span>
            <span className="text-xs sm:text-sm font-normal text-green-600 block sm:inline sm:ml-2">(Modo Editor)</span>
          </CardTitle>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Dialog open={isGuideModalOpen} onOpenChange={setIsGuideModalOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs sm:text-sm">Guía de Edición</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto mx-4">
                <DialogHeader>
                  <DialogTitle>Guía de Edición</DialogTitle>
                </DialogHeader>
                <MarkdownGuide />
              </DialogContent>
            </Dialog>

            {!isEditing ? (
              <Button onClick={() => setIsEditing(true)} variant="outline" size="sm" className="text-xs sm:text-sm flex-1 sm:flex-none">
                <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Editar
              </Button>
            ) : (
              <>
                <Button onClick={() => setIsEditing(false)} variant="outline" size="sm" className="text-xs sm:text-sm flex-1 sm:flex-none">
                  <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Vista previa
                </Button>
                <Button onClick={handleSave} disabled={isSaving} size="sm" className="text-xs sm:text-sm flex-1 sm:flex-none">
                  {isSaving ? <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" /> : <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />}
                  Guardar
                </Button>
              </>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium mb-2">
                  Título
                </label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Título de la página"
                  className="text-lg"
                />
              </div>

              <Tabs defaultValue="edit" className="w-full">
                <TabsList>
                  <TabsTrigger value="edit">Editar</TabsTrigger>
                  <TabsTrigger value="preview">Vista previa</TabsTrigger>
                </TabsList>

                <TabsContent value="edit" className="mt-4">
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="w-full h-64 sm:h-80 lg:h-96 p-3 sm:p-4 border border-gray-300 rounded-md font-mono text-xs sm:text-sm resize-none focus:ring-2 focus:ring-raime-golden-brown focus:border-transparent"
                    placeholder="Escribe tu contenido en Markdown aquí..."
                  />
                </TabsContent>

                <TabsContent value="preview" className="mt-4">
                  <div className="border border-gray-300 rounded-md p-3 sm:p-4 h-64 sm:h-80 lg:h-96 overflow-y-auto bg-gray-50">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({ children }) => (
                          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-raime-dark-blue mb-3 sm:mb-4 break-words">{children}</h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-raime-dark-blue mb-2 sm:mb-3 break-words">{children}</h2>
                        ),
                        p: ({ children }) => <p className="text-xs sm:text-sm lg:text-base text-gray-700 mb-2 sm:mb-3 break-words">{children}</p>,
                        a: ({ href, children }) => (
                          <a href={href} className="text-raime-golden-brown hover:underline break-words">
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {content}
                    </ReactMarkdown>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-raime-dark-blue mb-4 sm:mb-6 break-words">{title}</h1>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => (
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-raime-dark-blue mt-4 sm:mt-6 mb-3 sm:mb-4 break-words">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-raime-dark-blue mt-3 sm:mt-4 mb-2 sm:mb-3 break-words">{children}</h2>
                  ),
                  p: ({ children }) => <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3 sm:mb-4 break-words">{children}</p>,
                  a: ({ href, children }) => (
                    <a href={href} className="text-raime-golden-brown hover:text-raime-dark-blue underline break-words">
                      {children}
                    </a>
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}

          {lastModified && (
            <div className="mt-4 sm:mt-6 pt-4 border-t border-gray-200 text-xs sm:text-sm text-gray-500">
              Última modificación: {new Date(lastModified).toLocaleString("es-ES")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
